/**
 * A radix-2 FFT, and the inverse real transform built on it.
 *
 * There is no irfft in the platform and we need exactly one thing from it: turning a
 * measured magnitude spectrum back into the impulse response of a filter. That is a
 * handful of lines, so it lives here rather than pulling in a DSP library.
 */

/** In-place complex FFT. `re` and `im` must both be length n, n a power of two. */
export function fft(re: Float64Array, im: Float64Array, inverse = false): void {
  const n = re.length;
  if (n <= 1) return;
  if ((n & (n - 1)) !== 0) throw new Error(`fft: length ${n} is not a power of two`);

  // Bit-reversal permutation.
  for (let i = 1, j = 0; i < n; i++) {
    let bit = n >> 1;
    for (; j & bit; bit >>= 1) j ^= bit;
    j ^= bit;
    if (i < j) {
      [re[i], re[j]] = [re[j], re[i]];
      [im[i], im[j]] = [im[j], im[i]];
    }
  }

  const sign = inverse ? 1 : -1;
  for (let len = 2; len <= n; len <<= 1) {
    const ang = (sign * 2 * Math.PI) / len;
    const wr = Math.cos(ang);
    const wi = Math.sin(ang);
    for (let i = 0; i < n; i += len) {
      let cr = 1;
      let ci = 0;
      for (let k = 0; k < len / 2; k++) {
        const ur = re[i + k];
        const ui = im[i + k];
        const vr = re[i + k + len / 2] * cr - im[i + k + len / 2] * ci;
        const vi = re[i + k + len / 2] * ci + im[i + k + len / 2] * cr;
        re[i + k] = ur + vr;
        im[i + k] = ui + vi;
        re[i + k + len / 2] = ur - vr;
        im[i + k + len / 2] = ui - vi;
        const nr = cr * wr - ci * wi;
        ci = cr * wi + ci * wr;
        cr = nr;
      }
    }
  }

  if (inverse) {
    for (let i = 0; i < n; i++) { re[i] /= n; im[i] /= n; }
  }
}

/**
 * Inverse real FFT: `mag` holds n/2+1 non-negative magnitudes, phase is taken as zero.
 * Returns the n-sample real, zero-phase (and therefore symmetric) time signal.
 *
 * Zero phase is what we want here — the filter's phase response is irrelevant when the
 * input is white noise, and a symmetric response is the easiest to window cleanly.
 */
export function irfftZeroPhase(mag: Float32Array | Float64Array): Float64Array {
  const half = mag.length - 1;
  const n = half * 2;
  const re = new Float64Array(n);
  const im = new Float64Array(n);

  re[0] = mag[0];
  re[half] = mag[half];
  for (let k = 1; k < half; k++) {
    re[k] = mag[k];
    re[n - k] = mag[k];       // Hermitian symmetry with zero imaginary part
  }
  fft(re, im, true);
  return re;
}

/** Periodic Hann window of length n. */
export function hann(n: number): Float64Array {
  const w = new Float64Array(n);
  for (let i = 0; i < n; i++) w[i] = 0.5 - 0.5 * Math.cos((2 * Math.PI * i) / n);
  return w;
}
